import type React from 'react'
import { useCallback, useRef } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

/**
 * Live selected-shape ids captured when the edit starts (focus). A commit reads the eligible
 * shapes from the panel's last render, which can be stale by the time it runs: a canvas click
 * that moves the selection updates ShapeManager's selection imperatively and blurs the input
 * synchronously, before React re-renders with the new selection. Comparing a live read against
 * this snapshot (rather than trusting the render-time closure) is what lets a commit tell "the
 * selection changed under me" apart from "the same shapes are still selected."
 */
export function useSelectionSnapshotGuard(
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
) {
  const selectionSnapshotIdsRef = useRef<string[] | null>(null)

  /** Record which shapes are selected right now, so a later commit can detect the selection moved. */
  const captureSelectionSnapshot = useCallback(() => {
    const mgr = shapeManagerRef?.current

    selectionSnapshotIdsRef.current =
      typeof mgr?.getSelectedShapeIds === 'function' ? mgr.getSelectedShapeIds() : null
  }, [shapeManagerRef])

  /**
   * Whether the live canvas selection differs from the one captured at focus. If this component's
   * shape manager doesn't report a live selection at all (no ref wired, e.g. some standalone/test
   * usages), there's nothing to compare against, so treat it as unchanged rather than always
   * discarding.
   */
  const selectionChangedSinceSnapshot = useCallback((): boolean => {
    const mgr = shapeManagerRef?.current
    const snapshot = selectionSnapshotIdsRef.current

    if (snapshot == null || typeof mgr?.getSelectedShapeIds !== 'function') return false

    const current = mgr.getSelectedShapeIds()

    if (current.length !== snapshot.length) return true
    const snapshotSet = new Set(snapshot)

    return current.some((id) => !snapshotSet.has(id))
  }, [shapeManagerRef])

  return { captureSelectionSnapshot, selectionChangedSinceSnapshot }
}
